import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PRODUCT_TYPE_VOUCHER_MANAGER, SITE_DISPLAY_COMPLIMENTARY } from "@config/config";
import useAccess from "@hooks/useAccess";
import useBypassConfig from "@hooks/useBypassConfig";
import useContactDetails from "@hooks/useContactDetails";
import useModal from "@hooks/useModal";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { BookSessionContext } from "@context/BookSessionContext";
import AlertMessage from "@components/AlertMessage";
import FastAuthModal from "@components/Modal/FastAuthModal";
import Privacy from "@components/Privacy";
import Terms from "@components/Terms";
import Checkbox from "@components/Ui/Checkbox";
import ContactDetailsForm from "@components/V3/ContactDetailsForm";
import VoucherGifts from "@components/VoucherManager/VoucherGifts";
import SummaryTotal from "@components/VoucherManager/VoucherSummary/SummaryTotal";
import VoucherSummaryItems from "@components/VoucherManager/VoucherSummary/VoucherSummaryItems";
import "./style.scss";

const calcTotalPrice = (carry, item) => carry + item.price_cents * item.quantity;
const calcTotalQuantity = (carry, item) => carry + item.quantity;

const VoucherSummary = ({ items, type }) => {
    const {
        error,
        checkoutBookingSession,
        selectedItems,
        checkout,
        uuid,
        termsAccepted,
        setTermsAccepted,
    } = useContext(BookSessionContext);

    const { whiteLabel, productTypeId, publicSubscriberGroup } = useContext(ApplicationContext);
    const { drawerState, closeDrawer, user } = useContext(AuthContext);
    const { isFullyLoggedIn, canSignInOnSite } = useAccess();
    const { openModalWithContent, portal, closeModal } = useModal();

    const { isBypassActive, promptUserForEmail, shouldShowDisplayName } = useBypassConfig();

    const {
        purchaseEmail,
        setPurchaseEmail,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        emailError,
        setEmailError,
        validateContactEmail,
        isContactDetailsValid,
    } = useContactDetails({ isBypassActive, promptUserForEmail, shouldShowDisplayName, user });

    const [displayGiftingForm, setDisplayGiftingForm] = useState(false);
    const [giftData, setGiftData] = useState({
        recipientName: "",
        recipientEmail: "",
        giftMessage: "",
        leaveMessageBlank: false,
    });
    const [giftValidationErrors, setGiftValidationErrors] = useState({});

    const totalQuantity = items.reduce(calcTotalQuantity, 0);
    const totalPrice = items.reduce(calcTotalPrice, 0);
    const displayPrice = whiteLabel?.subscriber_group?.display_price;
    const displayGifting =
        whiteLabel?.enable_gifting === true &&
        publicSubscriberGroup === 0 &&
        productTypeId !== PRODUCT_TYPE_VOUCHER_MANAGER;

    useEffect(() => {
        setTermsAccepted(false);
    }, []);

    useEffect(() => {
        if (totalQuantity === 0 && drawerState.voucherCheckoutDrawer) {
            closeDrawer("voucherCheckoutDrawer");
        }
    }, [totalQuantity, drawerState.voucherCheckoutDrawer]);

    useEffect(() => {
        if (error || !checkout || totalQuantity === 0) return;

        // Only application security groups log in/sign up on our site
        if (!isFullyLoggedIn && canSignInOnSite) {
            openModalWithContent(
                <FastAuthModal uuid={uuid} totalPrice={totalPrice} />,
                "small"
            );
        } else {
            closeModal();
        }
    }, [isFullyLoggedIn, canSignInOnSite, checkout, uuid, error]);

    useEffect(() => {
        if (isBypassActive && !promptUserForEmail && user) {
            if (user.email_address
                && !user.email_address.includes("@noemail.internal")
                && !user.email_address.startsWith("support+")
                && !user.email_address.startsWith("temp-user+")) {
                setPurchaseEmail(user.email_address);
            }
            if (user.first_name) setFirstName(user.first_name);
            if (user.last_name) setLastName(user.last_name);
        }
    }, [isBypassActive, promptUserForEmail, user]);

    const validateGiftData = () => {
        const errors = {};

        if (!giftData.recipientName || giftData.recipientName.trim() === "") {
            errors.recipientName = "Recipient name is required";
        }

        if (!giftData.recipientEmail || giftData.recipientEmail.trim() === "") {
            errors.recipientEmail = "Recipient email is required";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(giftData.recipientEmail)) {
                errors.recipientEmail = "Please enter a valid email address";
            }
        }

        if (!giftData.leaveMessageBlank && (!giftData.giftMessage || giftData.giftMessage.trim() === "")) {
            errors.giftMessage = "Gift message is required, or check 'Leave message blank'";
        }

        return errors;
    };

    const handleCheckout = async () => {
        if (isBypassActive && !validateContactEmail()) return;

        setGiftValidationErrors({});

        if (displayGiftingForm) {
            const errors = validateGiftData();
            if (Object.keys(errors).length > 0) {
                setGiftValidationErrors(errors);
                return;
            }
        }

        if (totalQuantity > 0) {
            const giftingData = displayGiftingForm ? {
                isGift: true,
                giftRecipientName: giftData.recipientName,
                giftRecipientEmail: giftData.recipientEmail,
                giftMessage: giftData.leaveMessageBlank ? null : giftData.giftMessage,
            } : null;

            checkoutBookingSession(totalQuantity, type, giftingData, {
                purchaseEmail,
                firstName,
                lastName,
            });
        }
    };

    return (
        <div className="voucher-search__summary">
            {(totalPrice > 0 || displayPrice === SITE_DISPLAY_COMPLIMENTARY) && (
                <div className="voucher-search__summary-wrapper">
                    <VoucherSummaryItems
                        items={items}
                        selectedItems={selectedItems}
                        totalPrice={totalPrice}
                    />

                    {isBypassActive && (
                        <ContactDetailsForm
                            showDisplayName={shouldShowDisplayName}
                            firstName={firstName}
                            lastName={lastName}
                            purchaseEmail={purchaseEmail}
                            emailError={emailError}
                            onFirstNameChange={(e) => setFirstName(e.target.value)}
                            onLastNameChange={(e) => setLastName(e.target.value)}
                            onEmailChange={(e) => {
                                setPurchaseEmail(e.target.value);
                                setEmailError("");
                            }}
                            className="voucher-search__summary-form"
                            idPrefix="voucher"
                            required={isBypassActive}
                        />
                    )}

                    <div className="voucher-search__summary-terms">
                        {displayGifting ? (
                            <div className="voucher-search__summary-inner">
                                <Checkbox
                                    id="isGift"
                                    buttonAlignment="left"
                                    checked={displayGiftingForm}
                                    onChange={() => setDisplayGiftingForm(!displayGiftingForm)}
                                >
                                    This order is a gift
                                </Checkbox>
                            </div>
                        ) : null}

                        {displayGiftingForm ? (
                            <VoucherGifts
                                onGiftDataChange={setGiftData}
                                errorMessages={giftValidationErrors}
                            />
                        ) : null}

                        <div className="voucher-search__summary-inner">
                            <Checkbox
                                id="voucher-checkbox"
                                buttonAlignment="left"
                                checked={termsAccepted}
                                onChange={() => setTermsAccepted(!termsAccepted)}
                            >
                                {isBypassActive ? (
                                    <>
                                        I agree to the{" "}
                                        <a
                                            href="#terms"
                                            className="inline-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openModalWithContent(<Terms />, "medium");
                                            }}
                                        >
                                            terms and conditions of use
                                        </a>{" "}
                                        and the{" "}
                                        <a
                                            href="#privacy-collection-notice"
                                            className="inline-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openModalWithContent(<Privacy />, "medium");
                                            }}
                                        >
                                            privacy collection notice
                                        </a>.
                                    </>
                                ) : (
                                    <>
                                        I accept the{" "}
                                        <a
                                            href="#terms"
                                            className="inline-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openModalWithContent(<Terms />, "medium");
                                            }}
                                        >
                                            Terms &amp; Conditions
                                        </a>{" "}
                                        of purchase
                                    </>
                                )}
                            </Checkbox>
                        </div>

                        <SummaryTotal
                            items={items}
                            handleCheckout={handleCheckout}
                            isContactDetailsValid={isContactDetailsValid}
                        />

                        <div className="form__group-wrapper">
                            {error && <AlertMessage message={error} type="error" />}
                        </div>
                    </div>
                </div>
            )}
            {portal}
        </div>
    );
};

VoucherSummary.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            quantity: PropTypes.number.isRequired,
            price_cents: PropTypes.number.isRequired,
            rrp_price_cents: PropTypes.number,
            display_price_cents: PropTypes.number,
        })
    ).isRequired,
    type: PropTypes.string,
};

export default VoucherSummary;
